import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, ArrowDown, RotateCw } from "lucide-react";
import { ABtn, AScore, AResult, PadButton } from "./arcadeUi";
import { sfx } from "@/lib/soundfx";

const COLS = 10, ROWS = 16, CELL = 15, W = COLS * CELL, H = ROWS * CELL;
const SHAPES = {
  I: [[0, 1], [1, 1], [2, 1], [3, 1]],
  O: [[1, 0], [2, 0], [1, 1], [2, 1]],
  T: [[0, 1], [1, 1], [2, 1], [1, 0]],
  S: [[1, 0], [2, 0], [0, 1], [1, 1]],
  Z: [[0, 0], [1, 0], [1, 1], [2, 1]],
  J: [[0, 0], [0, 1], [1, 1], [2, 1]],
  L: [[2, 0], [0, 1], [1, 1], [2, 1]],
};
const COLORS = { I: "#22d3ee", O: "#e8b04b", T: "#a78bfa", S: "#22c55e", Z: "#ef4444", J: "#3b82f6", L: "#f97316" };
const KEYS = Object.keys(SHAPES);

const rotate = (cells) => {
  const maxY = Math.max(...cells.map((c) => c[1]));
  return cells.map(([x, y]) => [maxY - y, x]);
};

export default function BlockStacker() {
  const canvasRef = useRef(null);
  const g = useRef({});
  const scoreRef = useRef(0);
  const [score, setScore] = useState(0);
  const [status, setStatus] = useState("idle");

  const emptyGrid = () => Array.from({ length: ROWS }, () => Array(COLS).fill(null));
  const spawn = () => { const k = KEYS[(Math.random() * KEYS.length) | 0]; return { key: k, cells: SHAPES[k].map((c) => [...c]), px: 3, py: 0 }; };
  const collide = (grid, piece, ox = 0, oy = 0, cells = piece.cells) => cells.some(([x, y]) => {
    const gx = piece.px + x + ox, gy = piece.py + y + oy;
    return gx < 0 || gx >= COLS || gy >= ROWS || (gy >= 0 && grid[gy][gx]);
  });
  const merge = (s) => { s.piece.cells.forEach(([x, y]) => { const gy = s.piece.py + y, gx = s.piece.px + x; if (gy >= 0) s.grid[gy][gx] = COLORS[s.piece.key]; }); };
  const clearLines = (s) => {
    let n = 0;
    for (let r = ROWS - 1; r >= 0; r--) { if (s.grid[r].every((c) => c)) { s.grid.splice(r, 1); s.grid.unshift(Array(COLS).fill(null)); n++; r++; } }
    if (n) { scoreRef.current += [0, 40, 100, 300, 1200][n]; setScore(scoreRef.current); sfx.coin(); }
  };
  const start = () => { g.current = { grid: emptyGrid(), piece: spawn(), drop: 0, speed: 30 }; scoreRef.current = 0; setScore(0); setStatus("playing"); sfx.beep(); };

  const act = (type) => {
    const s = g.current; if (!s || !s.piece || status !== "playing") return;
    if (type === "left" && !collide(s.grid, s.piece, -1, 0)) s.piece.px--;
    else if (type === "right" && !collide(s.grid, s.piece, 1, 0)) s.piece.px++;
    else if (type === "down" && !collide(s.grid, s.piece, 0, 1)) { s.piece.py++; s.drop = 0; }
    else if (type === "rot") { const rc = rotate(s.piece.cells); if (!collide(s.grid, s.piece, 0, 0, rc)) { s.piece.cells = rc; sfx.click(); } }
  };

  useEffect(() => {
    const kd = (e) => {
      if (["ArrowLeft", "a"].includes(e.key)) act("left");
      else if (["ArrowRight", "d"].includes(e.key)) act("right");
      else if (["ArrowDown", "s"].includes(e.key)) act("down");
      else if (["ArrowUp", "w", " "].includes(e.key)) act("rot");
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", kd);
    return () => window.removeEventListener("keydown", kd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    const ctx = canvasRef.current.getContext("2d");
    const cell = (x, y, col) => { ctx.fillStyle = col; ctx.fillRect(x * CELL + 1, y * CELL + 1, CELL - 2, CELL - 2); };
    const draw = () => {
      ctx.fillStyle = "#0a0a12"; ctx.fillRect(0, 0, W, H);
      const s = g.current; if (!s.grid) return;
      s.grid.forEach((row, y) => row.forEach((c, x) => { if (c) cell(x, y, c); }));
      if (s.piece) s.piece.cells.forEach(([x, y]) => cell(s.piece.px + x, s.piece.py + y, COLORS[s.piece.key]));
    };
    if (status !== "playing") { draw(); return; }
    let raf;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const s = g.current;
      s.drop++;
      if (s.drop >= s.speed) {
        s.drop = 0;
        if (!collide(s.grid, s.piece, 0, 1)) s.piece.py++;
        else {
          merge(s); clearLines(s);
          s.speed = Math.max(8, 30 - Math.floor(scoreRef.current / 300));
          s.piece = spawn();
          if (collide(s.grid, s.piece, 0, 0)) { cancelAnimationFrame(raf); sfx.explode(); setStatus("over"); return; }
        }
      }
      draw();
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [status]);

  return (
    <div className="flex flex-col items-center">
      <div className="mb-2"><AScore label="Score" value={score} /></div>
      <div className="relative">
        <canvas ref={canvasRef} width={W} height={H} className="w-full max-w-[200px] rounded-md border-[3px] border-kirana-brown" style={{ imageRendering: "pixelated", touchAction: "none" }} />
        {status !== "playing" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-md bg-black/60 p-2">
            {status === "over" && <AResult win={false} text={`Over · ${score}`} testId="stacker-over" />}
            <ABtn tone="green" onClick={start} className="mt-3">{status === "idle" ? "Start" : "Play Again"}</ABtn>
          </div>
        )}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <PadButton testId="stacker-left" onPress={() => act("left")}><ArrowLeft className="h-5 w-5" /></PadButton>
        <PadButton testId="stacker-rot" onPress={() => act("rot")} className="bg-kirana-gold"><RotateCw className="h-5 w-5" /></PadButton>
        <PadButton testId="stacker-right" onPress={() => act("right")}><ArrowRight className="h-5 w-5" /></PadButton>
        <PadButton testId="stacker-down" onPress={() => act("down")}><ArrowDown className="h-5 w-5" /></PadButton>
      </div>
    </div>
  );
}
